import { differenceInCalendarDays, format, isValid, parseISO, startOfDay } from 'date-fns';

import { NULL_TIME } from '../../constants';

// The Permitting workspace model (docs/modules/permitting-workspace.md, Lifecycle).
// Pure: stages and their labels, the gates between them, when a request is Closed, and
// the words the screens use for blocked, due and expiring. No React, no API.

// --- Stages ----------------------------------------------------------------------------

export type StageKey =
  | 'requested'
  | 'preparing'
  | 'submitted'
  | 'issued'
  | 'inspections'
  | 'finalized';

interface StageDef {
  key: StageKey;
  label: string;
  /** The permit_status code for this stage. */
  code: string;
  /** The advance button that moves a permit INTO this stage ("Mark submitted"). */
  action: string;
}

// Stages match permit_status by code (kalos-core #2790), never by id or description: the
// "pending" row reads as Requested and the "in progress" row is coded preparing. There is
// no "waiting on COC" stage: waiting on the Certificate of Completion is a condition on an
// Inspections permit (waitingOnCertificateOfCompletion).
export const STAGES: StageDef[] = [
  { key: 'requested', label: 'Requested', code: 'pending', action: 'Mark requested' },
  { key: 'preparing', label: 'Preparing', code: 'preparing', action: 'Start preparing' },
  { key: 'submitted', label: 'Submitted', code: 'submitted', action: 'Mark submitted' },
  { key: 'issued', label: 'Issued', code: 'issued', action: 'Mark issued' },
  { key: 'inspections', label: 'Inspections', code: 'inspections', action: 'Move to Inspections' },
  { key: 'finalized', label: 'Finalized', code: 'finalized', action: 'Mark finalized' },
];

/** An outcome, not a stage: set from the stage menu, never a board column. */
export const NOT_REQUIRED_STATUS = 'not_required';

const normal = (value?: string | null) => (value ?? '').trim().toLowerCase();

// A stored date, or undefined for empty ("", 0001-01-01, 0000-00-00).
export const parseStoredDate = (value?: string | null) => {
  if (
    !value ||
    value === NULL_TIME ||
    value.startsWith('0000-00-00') ||
    value.startsWith('0001-01-01')
  ) {
    return undefined;
  }
  const parsed = parseISO(value);
  return isValid(parsed) ? parsed : undefined;
};

/** The stage for a permit_status code; undefined for expired, rejected and the like. */
export const stageOf = (statusCode?: string | null): StageKey | undefined =>
  STAGES.find((stage) => stage.code === normal(statusCode))?.key;

export const stageDef = (key: StageKey) => STAGES.find((stage) => stage.key === key)!;

// Statuses outside the six stages that still sit in one: Under review is a Submitted
// permit the department is looking at; Expired was Issued and needs renewing.
const STAGE_OF_STATUS: Record<string, StageKey> = { under_review: 'submitted', expired: 'issued' };

/** The stage a status sits in, counting Under review (Submitted) and Expired (Issued). */
export const placedStage = (statusCode?: string | null): StageKey | undefined =>
  stageOf(statusCode) ?? STAGE_OF_STATUS[normal(statusCode)];

const sentenceCase = (value: string) =>
  value ? value.charAt(0).toUpperCase() + value.slice(1) : value;

/** How a status reads: the stage's name, else its description sentence-cased ("Expired"). */
export const statusLabel = (statusCode?: string | null, description = '') => {
  const stage = stageOf(statusCode);
  if (stage) return stageDef(stage).label;
  if (normal(statusCode) === NOT_REQUIRED_STATUS) return 'Not required';
  // Without a description, the code reads as words: under_review → "Under review".
  return sentenceCase(description.trim() || normal(statusCode).replace(/_/g, ' '));
};

export const stageIndex = (key: StageKey) => STAGES.findIndex((stage) => stage.key === key);

export const nextStage = (key: StageKey): StageKey | undefined => STAGES[stageIndex(key) + 1]?.key;

/** The one backward move: corrections send a submitted permit back to Preparing. */
export const canSendBack = (key?: StageKey) => key === 'submitted';

/** A board move: one stage forward, or Submitted back to Preparing. */
export const isAllowedMove = (from: StageKey, to: StageKey) =>
  nextStage(from) === to || (canSendBack(from) && to === 'preparing');

export const isNotRequired = (statusCode?: string | null) =>
  normal(statusCode) === NOT_REQUIRED_STATUS;

/** Finalized, Not required or Rejected: nothing left to do on this permit. */
export const isDone = (statusCode?: string | null) =>
  stageOf(statusCode) === 'finalized' ||
  isNotRequired(statusCode) ||
  normal(statusCode) === 'rejected';

/** A request is Closed when every active permit is done (Finalized, Not required or Rejected). */
export const isGroupClosed = <P extends { isActive: boolean }>(
  permits: P[],
  statusCode: (permit: P) => string,
) => {
  const active = permits.filter((permit) => permit.isActive);
  return active.length > 0 && active.every((permit) => isDone(statusCode(permit)));
};

/** The status id to write for a stage (or Not required), from the permit_status list. */
export const statusIdForCode = (statuses: { id: number; code: string }[], code: string) =>
  statuses.find((status) => normal(status.code) === code)?.id;

export const statusIdForStage = (statuses: { id: number; code: string }[], key: StageKey) =>
  statusIdForCode(statuses, stageDef(key).code);

// --- Inspections -----------------------------------------------------------------------

export type InspectionResult =
  | 'pending'
  | 'passed'
  | 'failed'
  | 'cancelled'
  | 'submitted to provider'
  | 'pending correction'
  | 'partial'
  | 'no access';

/** Results that end an attempt without passing: each offers the next attempt. */
export const NEEDS_NEXT_ATTEMPT: InspectionResult[] = ['failed', 'partial', 'no access'];

/** An inspection_status code (kalos-core #2790) as a result. */
export const inspectionResult = (statusCode?: string | null): InspectionResult =>
  (
    ({
      pass: 'passed',
      fail: 'failed',
      cancelled: 'cancelled',
      submitted_to_provider: 'submitted to provider',
      pending_correction: 'pending correction',
      partial: 'partial',
      no_access: 'no access',
    }) as Record<string, InspectionResult>
  )[normal(statusCode)] ?? 'pending';

/** A result in the words the pages use ("Passed", "No access"). */
export const inspectionResultLabel = (statusCode?: string | null, description = '') =>
  sentenceCase(statusCode ? inspectionResult(statusCode) : description.trim());

/** The inspection_status code to write for a result. */
export const INSPECTION_RESULT_CODE: Record<InspectionResult, string> = {
  pending: 'pending',
  passed: 'pass',
  failed: 'fail',
  cancelled: 'cancelled',
  'submitted to provider': 'submitted_to_provider',
  'pending correction': 'pending_correction',
  partial: 'partial',
  'no access': 'no_access',
};

/** What the gates read on an inspection: its status code and its path code. */
export interface InspectionLike {
  id: number;
  statusCode: string;
  inspectionTypeId: number;
  inspectionType: string;
  /** The inspection_path code: county or private_provider ('' when not set). */
  inspectionPath: string;
}

// Each attempt is its own row. What counts for an inspection type is its latest
// attempt: a failed Rough-in followed by a passed one has passed. Cancelled types drop out.
const latestByType = (inspections: InspectionLike[]) => {
  const latest = new Map<string, InspectionLike>();
  for (const inspection of [...inspections].sort((a, b) => a.id - b.id)) {
    const type = inspection.inspectionTypeId
      ? `id:${inspection.inspectionTypeId}`
      : `text:${normal(inspection.inspectionType)}`;
    latest.set(type, inspection);
  }
  return [...latest.values()].filter(
    (inspection) => inspectionResult(inspection.statusCode) !== 'cancelled',
  );
};

// --- Gates -----------------------------------------------------------------------------

export interface GateInput {
  /** The permit_status code. */
  statusCode: string;
  permitNumber: string;
  issuedDate: string;
  expirationDate: string;
  applicationNumber: string;
  blockers: { name: string }[];
  inspections: InspectionLike[];
  /** The document_type codes of the permit's documents. */
  documentTypes: string[];
}

export interface GateContext {
  /** The request's owner (assigned user); 0 = unassigned. */
  ownerId: number;
}

export const CERTIFICATE_OF_COMPLETION_DOCUMENT_TYPE = 'certificate_of_completion';
export const PERMIT_CARD_DOCUMENT_TYPE = 'permit_card';
/** The words in a gate message that the screens turn into a link to Documents. */
export const PERMIT_CARD_STEP = 'add a permit card document';

// Why an inspection type hasn't passed, as the server words it ("it's partial").
const notPassedReason = (result: InspectionResult) =>
  ({
    failed: 'it failed',
    partial: "it's partial",
    'no access': 'no access',
    pending: 'no result yet',
    'submitted to provider': "it's with the provider",
    'pending correction': "it's pending a correction",
  })[result as string] ?? 'no result yet';

/**
 * The inspections that stand before Finalized: each type's latest attempt that hasn't
 * passed, with why ("it's partial"). Cancelled types drop out.
 */
export const unpassedInspections = <T extends InspectionLike>(inspections: T[]) =>
  (latestByType(inspections) as T[])
    .filter((inspection) => inspectionResult(inspection.statusCode) !== 'passed')
    .map((inspection) => ({
      inspection,
      type: inspection.inspectionType.trim() || 'inspection',
      reason: notPassedReason(inspectionResult(inspection.statusCode)),
    }));

/** Inspections that count toward Finalized (cancelled types dropped); 0 = none to finalize on. */
export const countedInspections = (inspections: InspectionLike[]) =>
  latestByType(inspections).length;

// Each inspection type whose latest attempt hasn't passed: "the latest Final inspection
// (it's partial)".
const inspectionSteps = (permit: GateInput) =>
  // As the server: a permit with no inspections isn't held by the passed rule.
  unpassedInspections(permit.inspections).map(
    ({ type, reason }) => `get a passed result on the latest ${type} inspection (${reason})`,
  );

/** What's missing to move a permit into `to`, one short step each; [] when the gate is met. */
export const gateSteps = (permit: GateInput, to: StageKey, { ownerId }: GateContext): string[] => {
  switch (to) {
    case 'preparing':
      return ownerId > 0 ? [] : ['set the owner on the permit request'];
    case 'submitted':
      return [
        permit.blockers.length > 0 &&
          `clear the open blockers (${permit.blockers.map((blocker) => blocker.name).join(', ')})`,
        !permit.applicationNumber.trim() && 'enter the application #',
      ].filter((step): step is string => !!step);
    case 'issued':
      return [
        !permit.permitNumber.trim() && 'enter the permit #',
        !parseStoredDate(permit.issuedDate) && 'enter the issued date',
        !parseStoredDate(permit.expirationDate) && 'enter the expiration date',
        !permit.documentTypes.includes(PERMIT_CARD_DOCUMENT_TYPE) && PERMIT_CARD_STEP,
      ].filter((step): step is string => !!step);
    case 'inspections':
      return permit.inspections.length > 0 ? [] : ['request an inspection'];
    case 'finalized': {
      const privateProvider = permit.inspections.some(
        (inspection) => inspection.inspectionPath === 'private_provider',
      );
      // As the server: with no inspection (or only cancelled ones) there is nothing
      // passed to finalize on.
      const counted = latestByType(permit.inspections);
      return [
        ...(counted.length === 0
          ? ['record at least one passed inspection, or mark the permit Not required']
          : inspectionSteps(permit)),
        privateProvider &&
          !permit.documentTypes.includes(CERTIFICATE_OF_COMPLETION_DOCUMENT_TYPE) &&
          'add a Certificate of Completion document (a private provider inspected it)',
      ].filter((step): step is string => !!step);
    }
    default:
      return [];
  }
};

/**
 * What stops a permit moving into `to` (docs/modules/permitting-workspace.md, Gates), in
 * the server's words: "To mark this permit Submitted, first: clear the open blockers
 * (NOC); enter the application #." Undefined when the gate is met.
 */
export const gateMessage = (permit: GateInput, to: StageKey, context: GateContext) => {
  const steps = gateSteps(permit, to, context);
  return steps.length
    ? `To ${to === 'inspections' ? 'move this permit to' : 'mark this permit'} ${stageDef(to).label}, first: ${steps.join('; ')}.`
    : undefined;
};

/** Every gate between the permit's stage and `to` (for a jump from the stage menu). */
export const gateMessageForJump = (permit: GateInput, to: StageKey, context: GateContext) => {
  const from = stageOf(permit.statusCode);
  if (!from || stageIndex(to) <= stageIndex(from)) return undefined;
  for (const stage of STAGES.slice(stageIndex(from) + 1, stageIndex(to) + 1)) {
    const message = gateMessage(permit, stage.key, context);
    if (message) return message;
  }
  return undefined;
};

// --- Words for the lists ---------------------------------------------------------------

/**
 * The blockers an NOC document clears: the ones named for the NOC ("NOC", "Pending NOC").
 * Uploading an NOC (or typing a document as NOC) clears them on that permit.
 */
export const nocBlockers = <T extends { name: string }>(blockers: T[]) =>
  blockers.filter((blocker) => /(^|\s)noc($|\s)/.test(normal(blocker.name)));

/** "Blocked: Pending NOC, NOC", or undefined. */
export const blockedText = (blockers: { name: string }[]) =>
  blockers.length ? `Blocked: ${blockers.map((blocker) => blocker.name).join(', ')}` : undefined;

const days = (count: number) => `${count} ${count === 1 ? 'day' : 'days'}`;

/** A next action's due date in words: "Overdue 2 days", "Due today", "Due Oct 2". */
export const dueText = (due?: string | null, now = new Date()) => {
  const date = parseStoredDate(due);
  if (!date) return undefined;
  const late = differenceInCalendarDays(startOfDay(now), startOfDay(date));
  if (late > 0) return { text: `Overdue ${days(late)}`, overdue: true };
  if (late === 0) return { text: 'Due today', overdue: false };
  return {
    text: `Due ${format(date, date.getFullYear() === now.getFullYear() ? 'MMM d' : 'MMM d, yyyy')}`,
    overdue: false,
  };
};

export const isOverdue = (due?: string | null, now = new Date()) => !!dueText(due, now)?.overdue;

/** Expiring = the expiration date is within this many days (placeholder, Angela to confirm). */
export const EXPIRING_DAYS = 30;

/** "Expires Oct 2" within 30 days, "Expired Sep 20" once past; undefined otherwise. */
export const expiryText = (expiration?: string | null, now = new Date()) => {
  const date = parseStoredDate(expiration);
  if (!date) return undefined;
  const left = differenceInCalendarDays(startOfDay(date), startOfDay(now));
  if (left < 0) {
    return { text: `Expired ${format(date, 'MMM d, yyyy')}`, soon: 'Expired', expired: true };
  }
  if (left <= EXPIRING_DAYS) {
    return {
      text: `Expires ${format(date, 'MMM d, yyyy')}`,
      /** Beside the date itself: how soon, not the date again. */
      soon: left === 0 ? 'Expires today' : `Expires in ${days(left)}`,
      expired: false,
    };
  }
  return undefined;
};

/** Sort key: overdue first, then the soonest due; no due date last. */
export const urgencyKey = (due?: string | null) => parseStoredDate(due)?.getTime() ?? Infinity;

// --- Residential or Commercial ---------------------------------------------------------

export type Sector = 'residential' | 'commercial';

// The department's sector group (Robbie question 1 proposal): 1 is Residential, the
// others are commercial sectors. A department with no sector falls back to its name.
export const sectorOf = (
  department?: { sectorGroup: number; description: string } | null,
): Sector | undefined => {
  if (!department) return undefined;
  if (department.sectorGroup === 1) return 'residential';
  if (department.sectorGroup > 1) return 'commercial';
  const name = normal(department.description);
  if (name.startsWith('residential')) return 'residential';
  if (name.startsWith('commercial')) return 'commercial';
  return undefined;
};

// --- Trades ----------------------------------------------------------------------------

interface DepartmentLike {
  id: number;
  description: string;
  sectorGroup: number;
}
interface TradeLike {
  id: number;
  name: string;
}

const SECTOR_WORDS = ['commercial', 'residential'];
// Whole words only: "IT/BAS" is one word, so "IT Web Development" never matches it.
const firstWord = (value: string) => normal(value).split(/\s+/)[0] ?? '';
const withoutSector = (value: string) =>
  normal(value).replace(new RegExp(`^(${SECTOR_WORDS.join('|')})\\s+`), '');

/**
 * The permit_trade row for a department (permit_trade is the older duplicate list): the
 * same name, the name without "Commercial"/"Residential", or the same first word
 * ("Steel And Welding" → "Steel Work"). Undefined when nothing matches.
 */
export const tradeForDepartment = (department: DepartmentLike, trades: TradeLike[]) => {
  const name = normal(department.description);
  const bare = withoutSector(department.description);
  const word = firstWord(bare);
  return (
    trades.find((trade) => normal(trade.name) === name) ??
    trades.find((trade) => normal(trade.name) === bare) ??
    (SECTOR_WORDS.includes(word)
      ? undefined
      : trades.find((trade) => firstWord(trade.name) === word))
  );
};

/**
 * Departments a permit can be for: the ones with a sector (Residential HVAC, the
 * commercial trades) and the construction trades that match a permit trade by name.
 * Office departments (Finance, Sales) are neither.
 */
export const isTradeDepartment = (department: DepartmentLike, trades: TradeLike[]) =>
  department.sectorGroup > 0 || !!tradeForDepartment(department, trades);

/**
 * Residential or Commercial for a trade department: its sector group, else Commercial
 * for the construction trades (they have no sector and are commercial work).
 */
export const tradeSector = (department: DepartmentLike, trades: TradeLike[]): Sector | undefined =>
  sectorOf(department) ?? (isTradeDepartment(department, trades) ? 'commercial' : undefined);

/** The department for an older permit that only has a trade. */
export const departmentForTrade = (trade: TradeLike, departments: DepartmentLike[]) =>
  departments.find((department) => tradeForDepartment(department, [trade])?.id === trade.id);

// --- Expiration after a passed inspection ------------------------------------------------

/** A passed inspection renews the permit this many days from the inspection. */
export const RENEWAL_DAYS = 180;

/**
 * The expiration a passed inspection suggests: the inspection's date (the confirmed
 * booking, else the requested date, else today) plus 180 days. Only a suggestion: the
 * person confirms it in Record result, or keeps the current date.
 */
export const suggestedExpiration = (
  inspection: { scheduledDate: string; requestorScheduledDate: string },
  today = new Date(),
) => {
  const inspected =
    parseStoredDate(inspection.scheduledDate) ??
    parseStoredDate(inspection.requestorScheduledDate) ??
    today;
  const next = startOfDay(inspected);
  next.setDate(next.getDate() + RENEWAL_DAYS);
  return next;
};

/**
 * "Waiting on COC": a permit in Inspections whose only step left before Finalized is the
 * Certificate of Completion (every inspection has passed; a private provider inspected it).
 */
export const WAITING_ON_CERTIFICATE_OF_COMPLETION = 'Waiting on COC';
export const waitingOnCertificateOfCompletion = (permit: GateInput, context: GateContext) => {
  if (stageOf(permit.statusCode) !== 'inspections') return false;
  const steps = gateSteps(permit, 'finalized', context);
  return steps.length === 1 && steps[0]!.startsWith('add a Certificate of Completion');
};
